// src/components/CustomFields/fields/ImageField.tsx
// ImageField component migrated from legacy

import React from 'react';
import CustomFieldsService from '../../../api/CustomFieldsService';
import * as APIUtils from '../../Globals/Utils';
import './ImageField.css';
import type { ImageFieldProps, CustomFieldItem } from './types';

interface ImageFieldState {
  canEdit: boolean;
  customField: CustomFieldItem;
  editorEnabled: boolean;
  api: { url: string; token: string };
  binaryImage: string;
  AssociatedImage: any;
}

class ImageField extends React.Component<ImageFieldProps, ImageFieldState>
{
  private corgeeImageFieldRef = React.createRef<HTMLDivElement>();
  private customImageUploaderRef = React.createRef<HTMLInputElement>();
  private defaultBinaryImage: string;

  constructor(props: ImageFieldProps)
  {
    super(props);
    // Normalize URL to avoid double slashes
    const baseUrl = props.apiConfig.url.replace(/\/$/, '');
    this.defaultBinaryImage = baseUrl + '/Images/icon-photo.png';

    this.state = {
      canEdit: props.canEdit && props.customField.ConversationIsEnable !== false,
      customField: props.customField,
      editorEnabled: false,
      api: {
        url: props.apiConfig.url,
        token: props.apiConfig.token,
      },
      binaryImage:
        (props.customField as any).binary === undefined ||
        (props.customField as any).binary === null ||
        (props.customField as any).binary === ''
          ? this.defaultBinaryImage
          : (props.customField as any).binary,
      AssociatedImage: null,
    };

    this.enableEditor = this.enableEditor.bind(this);
    this.getImageResult = this.getImageResult.bind(this);
    this.onImageOver = this.onImageOver.bind(this);
    this.onSelectImage = this.onSelectImage.bind(this);
    this.uploadImagePost = this.uploadImagePost.bind(this);
    this.saveCustomFieldValue = this.saveCustomFieldValue.bind(this);
    this.onUpdateCustomField = this.onUpdateCustomField.bind(this);
  }

  componentWillReceiveProps(nextProps: ImageFieldProps)
  {
    // Handle in-memory updates (blob URLs)
    const nextValueIsUrl =
      typeof nextProps.customField.Value === 'string' &&
      nextProps.customField.Value.startsWith('blob:');
    const nextBinaryIsUrl =
      typeof (nextProps.customField as any).binary === 'string' &&
      (nextProps.customField as any).binary.startsWith('blob:');

    if (nextValueIsUrl)
    {
      this.setState({
        customField: nextProps.customField,
        binaryImage: nextProps.customField.Value as string,
      });
      return;
    }

    if (nextBinaryIsUrl)
    {
      this.setState({
        customField: nextProps.customField,
        binaryImage: (nextProps.customField as any).binary,
      });
      return;
    }

    if ((nextProps.customField as any).binary !== undefined)
    {
      this.setState({
        customField: nextProps.customField,
        binaryImage:
          (nextProps.customField as any).binary === undefined ||
          (nextProps.customField as any).binary === null ||
          (nextProps.customField as any).binary === ''
            ? this.defaultBinaryImage
            : (nextProps.customField as any).binary,
      });
    }

    // Update image when is a new image and value is null
    if (
      this.state.customField &&
      this.state.customField.Value === null &&
      nextProps.customField &&
      nextProps.customField.Value !== null
    )
    {
      const customFieldFix = { ...this.state.customField };
      customFieldFix.CustomFieldValueId = nextProps.customField.CustomFieldValueId;
      customFieldFix.Value = nextProps.customField.Value;
      this.setState({ customField: customFieldFix }, this.getImageResult);
    }
  }

  componentDidMount()
  {
    const { customField, binaryImage } = this.state;

    // If it's a blob, it's a local optimistic update. Don't fetch from server.
    if (typeof binaryImage === 'string' && binaryImage.startsWith('blob:'))
    {
      return;
    }

    if (
      customField.Value !== null &&
      customField.Value !== '' &&
      customField.Value !== undefined &&
      customField.Value !== 0
    )
    {
      this.getImageResult();
    }
  }

  getImageResult()
  {
    const requestConfig = {
      api: this.state.api,
      request: this.state.customField.Value,
    };
    CustomFieldsService.getImageResult(requestConfig as any).then((data: any) =>
    {
      this.setState({ AssociatedImage: data });
      this.setState({ binaryImage: data.ThumbnailPath });
    });
  }

  enableEditor()
  {
    this.setState({ editorEnabled: !this.state.editorEnabled });
  }

  onImageOver()
  {
    if (this.state.canEdit)
    {
      this.setState({ editorEnabled: true });
    }
  }

  onSelectImage(e: React.ChangeEvent<HTMLInputElement>)
  {
    let files: FileList | null = null;
    if (e.target) files = e.target.files;

    const { disableAutoSave } = this.props;

    if (files !== null && files !== undefined && files.length > 0)
    {
      const dataRequest = new FormData();
      dataRequest.append('file', files[0]!);
      dataRequest.append(
        'customFieldValueId',
        String(this.props.customField.CustomFieldValueId || 0),
      );

      if (!disableAutoSave)
      {
        this.uploadImagePost(dataRequest, 'uploadimage').then((dataResponse: any) =>
        {
          this.setState({ AssociatedImage: dataResponse });
          this.saveCustomFieldValue(dataResponse, files![0]!);
        });
      }
      else
      {
        this.uploadImagePost(dataRequest, 'UploadImageWithoutSaving').then((dataResponse: any) =>
        {
          this.setState({ AssociatedImage: dataResponse });
          this.saveCustomFieldValue(dataResponse, files![0]!);
        });
      }
    }
  }

  saveCustomFieldValue(customImage: any, _file: File)
  {
    const { disableAutoSave } = this.props;

    const copyCustomField: any = { ...this.state.customField };
    copyCustomField.Value = customImage.CustomImageId;
    copyCustomField.binary = customImage.ThumbnailPath;

    const requestSave = {
      api: this.state.api,
      request: copyCustomField,
    };

    if (copyCustomField.CustomFieldValueId === 0 && !disableAutoSave)
    {
      CustomFieldsService.savePost(requestSave).then((data: any) =>
      {
        copyCustomField.CustomFieldValueId = data.CustomFieldValueId;
        copyCustomField.binaryImage = data.ThumbnailPath;
        this.setState({ customField: copyCustomField, binaryImage: data.ThumbnailPath }, () =>
        {
          this.onUpdateCustomField();
        });
      });
    }
    else
    {
      copyCustomField.AssociatedImage = this.state.AssociatedImage;

      this.setState(
        { customField: copyCustomField, binaryImage: this.state.AssociatedImage.ThumbnailPath },
        () =>
        {
          this.onUpdateCustomField();
        },
      );
    }
  }

  uploadImagePost(dataRequest: FormData, apiMethod: string): Promise<any>
  {
    return new Promise((resolve, reject) =>
    {
      APIUtils.callFetch(
        this.props.apiConfig,
        'api/customfieldvalue/' + apiMethod,
        'POST',
        dataRequest,
        undefined,
        true,
      )
        .then((response: any) =>
        {
          if (response.ok)
          {
            return response.json();
          }
          else if (response.status === 401)
          {
            return response;
          }
          else
          {
            return response;
          }
        })
        .then((response: any) =>
        {
          if (response.ok !== undefined && response.ok === false && response.status === 401)
          {
            reject({
              redirectLogout: true,
              status: response.status,
              statusText: response.statusText,
            });
          }
          else
          {
            resolve({ ...response });
          }
        })
        .catch((error: Error) =>
        {
          throw error;
        });
    });
  }

  onUpdateCustomField()
  {
    if (this.props.UpdateCustomFields)
    {
      this.props.UpdateCustomFields(this.state.customField);
    }
  }

  render()
  {
    const { customField, canEdit, editorEnabled, binaryImage } = this.state;

    return (
      <div
        ref={this.corgeeImageFieldRef}
        className="corgee_com_custom-fields-field-A _DEL_field-A custom-field-id"
      >
        {customField.GroupMultiEditionType === 1 && (
          <i
            className="material-icons several-values enable-help-tooltip"
            title="Not all cards selected have this detail. The detail value will change only on applicable cards."
            data-title="Not all cards selected have this detail. The detail value will change only on applicable cards."
          >
            do_not_disturb_on
          </i>
        )}
        <span className="corgee_com_custom-fields-title _DEL_title">{customField.Name}</span>
        {!customField.GroupMultiEditionType && (
          <i
            className="material-icons not-filterable enable-help-tooltip"
            data-title="This detail type is not filterable"
          >
            do_not_disturb_on
          </i>
        )}

        <div className="corgee_com_custom-photo-uploader _DEL_custom-photo-uploader">
          <input
            ref={this.customImageUploaderRef}
            type="file"
            name="customImageUploader"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={this.onSelectImage}
          />
          <div
            className="corgee_com_custom-photo-uploader-picture _DEL_custom-photo-uploader-picture"
            onMouseLeave={() => this.setState({ editorEnabled: false })}
          >
            <div
              className="corgee_com_custom-photo-uploader-image-container _DEL_custom-photo-uploader-image-container"
              style={{ backgroundImage: 'url(' + binaryImage + ')' }}
              onMouseOver={this.onImageOver}
            ></div>
            {editorEnabled && canEdit ? (
              <span
                role="button"
                tabIndex={0}
                className="action"
                onClick={() => this.customImageUploaderRef.current?.click()}
              >
                <span className="icon-pencil-icon"></span>
              </span>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
}

export default ImageField;
